import { useState, useMemo, useContext } from "react";
import { Button, Table, Form, InputGroup, Pagination, Badge, Col } from "react-bootstrap";
import { Search, Edit3, Trash2, Eye, MapPin, Plus, Clock } from "lucide-react";
import { ToastContainer, toast, Zoom } from 'react-toastify';
import { Dimcontext } from "../../context/pagecontext";
import { Datacontext } from "../../context/dataContext";
import { motion, AnimatePresence } from "framer-motion";
import '../css/user/userreg.css'; 
import { AddBranch } from "./add_branch";
import { EditBranch } from "./edit_branch"; // Fixed space in your import
import { ViewBranch } from "./view_branch";

// Added api and Swal imports for the delete function
import api from '../../api';
import Swal from 'sweetalert2';

export const Branch = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [modalState, setModalState] = useState({ type: null, id: null });
  
  const { setDim } = useContext(Dimcontext);
  const { branches = [], fetchBranches } = useContext(Datacontext); 

  const filteredBranches = useMemo(() => {
    return branches.filter(b => b.name?.toLowerCase().includes(searchTerm.toLowerCase().trim()));
  }, [branches, searchTerm]);

  const totalPages = Math.ceil(filteredBranches.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredBranches.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);
  const showToast = (msg) => toast.warn(msg, { theme: "dark", transition: Zoom, position: "bottom-center" });
  
  const handleRowsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1); 
  };

  const openModal = (type, id = null) => {
    setModalState({ type, id });
    setDim(true);
  };

  // --- NEW: Delete Handler with SweetAlert2 Confirmation ---
  const handleDelete = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33", // Red for confirm (delete)
      cancelButtonColor: "#6c757d", // Gray for cancel
      confirmButtonText: "Yes, delete it!"
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          // Adjust the endpoint route to match your backend setup
          await api.delete(`/deletebranch/${id}`);
          
          Swal.fire({
            title: "Deleted!",
            text: "The branch has been removed.",
            icon: "success",
            timer: 2000,
            showConfirmButton: false
          });
          
          if (fetchBranches) fetchBranches(); // Refresh table data
        } catch (error) {
          console.error("Delete error:", error);
          showToast(error.response?.data?.message || "Failed to delete branch");
        }
      }
    });
  };

  return (
    <div className="document-container p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="page-title">Branch Management</h3>
        <div className="d-flex flex-column flex-md-row gap-3 align-items-md-center mb-4">
          <Col xs={12} md={5} lg={4}>
            <InputGroup className="search-bar-container">
              <InputGroup.Text className="bg-white border-end-0">
                <Search size={18} className="text-muted" />
              </InputGroup.Text>
              <Form.Control 
                className="border-start-0 ps-0"
                placeholder="Search Branch..." 
                value={searchTerm} 
                onChange={(e) => {setSearchTerm(e.target.value); setCurrentPage(1);}}
              />
            </InputGroup>
          </Col>
          
          <Button className="btn-add d-flex align-items-center justify-content-center gap-2" onClick={() => openModal('add')}>
            <Plus size={18} /> <span>Add Branch</span>
          </Button>
        </div>
      </div>

      <div className="table-card shadow-sm">
        <Table hover striped responsive className="m-0">
          <thead>
            <tr>
              <th className="text-center" width="80">No.</th>
              <th>Branch Name</th>
              <th>Shift</th>
              <th>Radius (m)</th>
              <th className="text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentItems.map((b, index) => (
              <tr key={b.id}>
                <td className="text-center">{indexOfFirstItem + index + 1}</td>
                <td className="fw-semibold text-uppercase">
                  <MapPin size={14} className="me-2 text-muted"/>
                  {b.name}
                </td>
                <td>
                  <Badge bg="info" className="text-uppercase d-inline-flex align-items-center gap-1">
                    <Clock size={12} /> {b.shift}
                  </Badge>
                </td>
                <td className="text-muted">{b.radius}</td>
                <td>
                  <div className="d-flex justify-content-center gap-2">
                    <Button variant="outline-success" size="sm" onClick={() => openModal('view', b.id)}><Eye size={16}/></Button>
                    <Button variant="outline-primary" size="sm" onClick={() => openModal('edit', b.id)}><Edit3 size={16}/></Button>
                    
                    {/* Updated Delete Button onClick to trigger the popup directly */}
                    <Button variant="outline-danger" size="sm" onClick={() => handleDelete(b.id)}>
                      <Trash2 size={16}/>
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {/* Standardized Pagination Footer */}
        <div className="pagination-footer d-flex flex-column flex-md-row justify-content-between align-items-center p-3 border-top bg-light gap-3">
            <div className="d-flex align-items-center gap-2">
                <span className="text-muted small">Rows per page:</span>
                <Form.Select 
                    size="sm" 
                    style={{ width: '80px', borderRadius: '6px' }}
                    value={itemsPerPage} 
                    onChange={handleRowsPerPageChange}
                >
                    {[5, 10, 20, 50].map(v => (
                        <option key={v} value={v}>{v}</option>
                    ))}
                </Form.Select>
            </div>

            <div className="text-muted small fw-medium">
                {filteredBranches.length > 0 ? (
                    <>
                        Showing <b>{indexOfFirstItem + 1}</b> to <b>{Math.min(indexOfLastItem, filteredBranches.length)}</b> of <b>{filteredBranches.length}</b> branches
                    </>
                ) : (
                    "No branches found"
                )}
            </div>

            <Pagination className="mb-0 custom-pagination shadow-sm">
                <Pagination.First 
                    disabled={currentPage === 1} 
                    onClick={() => handlePageChange(1)} 
                />
                <Pagination.Prev 
                    disabled={currentPage === 1} 
                    onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
                />
                <Pagination.Item active className="px-2">{currentPage}</Pagination.Item>
                <Pagination.Next 
                    disabled={currentPage === totalPages || totalPages === 0} 
                    onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages))}
                />
                <Pagination.Last 
                    disabled={currentPage === totalPages || totalPages === 0} 
                    onClick={() => handlePageChange(totalPages)} 
                />
            </Pagination>
        </div>
      </div>

      <ToastContainer />
      
      <AnimatePresence mode="wait">
        {modalState.type && (
          <div className="custom-modal-backdrop">
            <motion.div 
              initial={{ y: 50, opacity: 0 }} 
              animate={{ y: 0, opacity: 1 }} 
              exit={{ y: 50, opacity: 0 }} 
              className="modal-content-wrapper"
            >
              {modalState.type === 'add' && <AddBranch set_add={() => setModalState({})} fetchBranches={fetchBranches} showToast={showToast} branches={branches}/>}
              {modalState.type === 'edit' && <EditBranch set_edit={() => setModalState({})} id={modalState.id} fetchBranches={fetchBranches} showToast={showToast}/>}
              {modalState.type === 'view' && <ViewBranch set_view={() => setModalState({})} id={modalState.id}/>}
            </motion.div>
          </div>
        )}
      </AnimatePresence> 
    </div>
  );
};